import React from 'react';
import Icon from '@/components/ui/AppIcon';


interface CaseType {
  title: string;
  description: string;
}

interface Benefit {
  title: string;
  description: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface PracticeAreaCardProps {
  icon: string;
  title: string;
  description: string;
  caseTypes: CaseType[];
  benefits: Benefit[];
  faqs: FAQ[];
  timeline: string;
  feeStructure: string;
  caseStudy: {
    title: string;
    description: string;
    result: string;
  };
  ctaText: string;
  onCtaClick: () => void;
}

const PracticeAreaCard: React.FC<PracticeAreaCardProps> = ({
  icon,
  title,
  description,
  caseTypes,
  benefits,
  faqs,
  timeline,
  feeStructure,
  caseStudy,
  ctaText,
  onCtaClick,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-elevation-sm hover:shadow-elevation-md transition-all duration-300 overflow-hidden">
      <div className="bg-gradient-to-r from-primary to-navy-dark p-8">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center">
            <Icon name={icon as any} size={32} className="text-primary" />
          </div>
          <h2 className="text-3xl font-serif font-bold text-white">{title}</h2>
        </div>
        <p className="text-white/90 text-lg leading-relaxed">{description}</p>
      </div>

      <div className="p-8 space-y-8">
        {/* Case Types Section */}
        <div>
          <h3 className="text-2xl font-serif font-semibold text-primary mb-4 flex items-center">
            <Icon name="DocumentTextIcon" size={24} className="text-accent mr-3" />
            Case Types We Handle
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {caseTypes.map((caseType, index) => (
              <div
                key={index}
                className="bg-surface p-4 rounded-lg border border-border hover:border-accent transition-colors duration-300"
              >
                <h4 className="font-semibold text-primary mb-2">{caseType.title}</h4>
                <p className="text-text-secondary text-sm">{caseType.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div>
          <h3 className="text-2xl font-serif font-semibold text-primary mb-4 flex items-center">
            <Icon name="CheckCircleIcon" size={24} className="text-accent mr-3" />
            Why Choose Us
          </h3>
          <div className="space-y-3">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Icon name="CheckIcon" size={20} className="text-success mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-primary">{benefit.title}</h4>
                  <p className="text-text-secondary text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline & Fee Structure */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-surface p-6 rounded-lg border border-border">
            <div className="flex items-center mb-3">
              <Icon name="ClockIcon" size={24} className="text-accent mr-3" />
              <h4 className="font-semibold text-primary">Typical Timeline</h4>
            </div>
            <p className="text-text-secondary">{timeline}</p>
          </div>
          <div className="bg-surface p-6 rounded-lg border border-border">
            <div className="flex items-center mb-3">
              <Icon name="CurrencyDollarIcon" size={24} className="text-accent mr-3" />
              <h4 className="font-semibold text-primary">Fee Structure</h4>
            </div>
            <p className="text-text-secondary">{feeStructure}</p>
          </div>
        </div>

        {/* Case Study */}
        <div className="bg-gradient-to-br from-accent/10 to-gold-dark/10 p-6 rounded-lg border border-accent/20">
          <div className="flex items-center mb-4">
            <Icon name="TrophyIcon" size={24} className="text-accent mr-3" />
            <h4 className="font-semibold text-primary text-lg">Success Story</h4>
          </div>
          <h5 className="font-semibold text-primary mb-2">{caseStudy.title}</h5>
          <p className="text-text-secondary mb-3">{caseStudy.description}</p>
          <div className="bg-white p-4 rounded-lg border-l-4 border-success">
            <p className="text-sm font-semibold text-success">Result: {caseStudy.result}</p>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onCtaClick}
          className="w-full px-8 py-4 bg-gradient-to-r from-accent to-gold-dark text-primary font-semibold text-lg rounded-lg shadow-subtle hover:shadow-elevation-sm hover:scale-102 transition-all duration-300 flex items-center justify-center space-x-2"
        >
          <span>{ctaText}</span>
          <Icon name="ArrowRightIcon" size={20} />
        </button>
      </div>
    </div>
  );
};

export default PracticeAreaCard;